import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { OrganizationKind } from '@/enums/organization'
import { useAppStore } from '@/stores/app'
import { useFetchStore } from '@/stores/fetch'
import type { IOrganization } from '@/types/Organization'

/*
 * The register of organisations, for the operator's people only — the API refuses it to
 * everybody else, so it is never asked for them. It feeds the customer switcher in the
 * topbar, the customer column of the vehicle lists, the customer select of the add-
 * vehicle dialog and the organisation column and select of the users page: one read,
 * shared, instead of one per screen.
 */
export const useOrganizationsStore = defineStore('organizations', () => {
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()

    const organizations = ref<IOrganization[]>([])

    /** Every customer, active or not — a name to print beside an old vehicle. */
    const customers = computed((): IOrganization[] =>
        organizations.value.filter((organization) => organization.kind === OrganizationKind.Customer))

    /** The customers that may be switched to and given a vehicle. */
    const activeCustomers = computed((): IOrganization[] => customers.value.filter((customer) => customer.active))

    const load = async () => {
        if (!appStore.isOperator()) return

        const { statusCode, data } = await fetchStore.fetch('/organizations/').get().json<IOrganization[]>()

        if (statusCode.value === 200) organizations.value = data.value ?? []

        // A remembered customer that no longer exists, or was switched off, is not a
        // view anybody can be left in.
        const chosen = appStore.customerId

        if (chosen && !activeCustomers.value.some((customer) => customer.id === chosen)) appStore.setCustomerId('')
    }

    /** An organisation's name by id, or nothing for an id nobody knows. */
    const nameOf = (id: string): string => organizations.value.find((organization) => organization.id === id)?.name ?? ''

    return { organizations, customers, activeCustomers, load, nameOf }
})
